/**
 * Trailer Modal Component
 * Opens YouTube trailers in a modal overlay
 */

import { useEffect, useCallback } from '@wordpress/element';

export default function TrailerModal({ film, isOpen, onClose }) {
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            onClose();
        }
    }, [onClose]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        };
    }, [isOpen, handleKeyDown]);

    if (!isOpen || !film) return null;

    const handleOverlayClick = (e) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    // Get embed URL
    let embedUrl = film.trailer_embed;
    if (!embedUrl && film.trailer_url) {
        // Convert watch URL to embed URL
        const match = film.trailer_url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
        if (match) {
            embedUrl = `https://www.youtube.com/embed/${match[1]}?autoplay=1&rel=0`;
        }
    }

    // Add autoplay if not already present
    if (embedUrl && !embedUrl.includes('autoplay')) {
        embedUrl += (embedUrl.includes('?') ? '&' : '?') + 'autoplay=1&rel=0';
    }

    const formatDuration = (mins) => {
        if (!mins || mins <= 0) return null;
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        if (h > 0 && m > 0) return `${h}h ${m}m`;
        if (h > 0) return `${h}h`;
        return `${m}m`;
    };

    return (
        <div
            className={`rialto-modal-overlay ${isOpen ? 'active' : ''}`}
            onClick={handleOverlayClick}
        >
            <div className="rialto-modal rialto-modal-large">
                <button className="rialto-modal-close" onClick={onClose}>
                    ×
                </button>

                {embedUrl && (
                    <div className="rialto-modal-video">
                        <iframe
                            src={embedUrl}
                            title={`${film.title} Trailer`}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                )}

                <div className="rialto-modal-info">
                    <div className="rialto-modal-info-content">
                        <h2 className="rialto-modal-title">{film.title}</h2>
                        <div className="rialto-modal-meta">
                            {film.rating && (
                                <span className="rialto-modal-rating">
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="#f5c518">
                                        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                    </svg>
                                    {parseFloat(film.rating).toFixed(1)}
                                </span>
                            )}
                            {film.year && <span>{film.year}</span>}
                            {film.duration > 0 && <span>{formatDuration(film.duration)}</span>}
                            {film.certification && <span>{film.certification}</span>}
                            {film.genres?.length > 0 && <span>{film.genres.join(', ')}</span>}
                        </div>
                        {film.synopsis && (
                            <p className="rialto-modal-description">{film.synopsis}</p>
                        )}
                    </div>
                    {film.url && (
                        <div className="rialto-modal-actions">
                            <a href={film.url} className="rialto-btn rialto-btn-gold">
                                {film._linkText || 'More Details'}
                            </a>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
